import {putAll, takeLatestF} from '@fl/cmsch-fe-library';
import {SagaIterator} from 'redux-saga';
import {isFull, none, opt} from 'ts-opt';
import {call, put, select} from 'typed-redux-saga';

import {Api} from 'api/gen/Api';
import {tCommon} from 'app/translations';
import {showBeError} from 'utils/show-be-error';
import {
    convertTableDataParamsToDto,
    getRequestedTableColumnSettings,
    getRequestedTablePageParams,
    simpleTableSelector,
    tablesAction,
} from 'utils/tables';

import {ManagementOtherSubjectsPage} from '../../types/management-other-subjects';
import {adminAction, GetManagementOtherSubjectsTableDataAction} from '../action';

const tableId = 'managementOtherSubjects';
const blankPageModel: ManagementOtherSubjectsPage = {
    content: [],
    page: {
        number: 1,
        size: 1,
        totalElements: 1,
        totalPages: 1,
    },
};

// eslint-disable-next-line max-lines-per-function
function* execute(action: GetManagementOtherSubjectsTableDataAction): SagaIterator {
    const {tableDataRequest, otherSubjectId} = action.payload;
    if (tableDataRequest.isEmpty) return yield* put(adminAction.getManagementOtherSubjectsTableDataSuccess(none));

    const tableState = yield* select(simpleTableSelector.tableState(tableId));
    const tableDataParams = getRequestedTablePageParams({
        tableId,
        tableDataRequest: tableDataRequest.orCrash('No tableDataRequest for management otherSubjects table'),
        tableState,
        resetType: 'tableDataParams',
    });

    if (otherSubjectId.isEmpty) {
        yield* put(adminAction.getManagementOtherSubjectsTableDataSuccess(opt(blankPageModel)));
        return yield* put(tablesAction.tableDataFetchSuccess(tableId, tableDataParams));
    }
    yield* put(tablesAction.tableDataFetchStart(tableId));

    const columnSettings = getRequestedTableColumnSettings<'managementOtherSubjects'>(tableState);
    const {filters, params} = convertTableDataParamsToDto({tableDataParams, columnSettings});
    const correctedFilter = opt(filters)
        .mapIn(x => (x.tag === 'CONTAINS_STRING' && x.field === 'username') ? x.value : null)
        .filterIn(isFull)
        .headIn()
        .orNull();
    const correctedParams = {sort: opt(params.sort).orElse([]), usernameFilterText: correctedFilter};

    const response = yield* call(
        Api.getSubjectOtherSubjectManagementPage,
        correctedParams,
        {otherSubjectId: otherSubjectId.orCrash('OtherSubjectId ID not found')});

    if (response.isSuccess) {
        const {headers, pagedModel} = response.data;
        yield* put(adminAction.getManagementOtherSubjectsTableDataSuccess(opt(pagedModel)));
        yield* put(adminAction.setManagementOtherSubjectsExtraColumns(headers || []));
        yield* put(tablesAction.tableDataFetchSuccess(tableId, tableDataParams));
    } else {
        yield putAll(showBeError(response, tCommon('tableLoadErrorTitle')));
        yield* put(tablesAction.tableDataFetchFailure(tableId));
    }
}

/** Runs the admin saga that loads other-subject-management table data. */
export function* getManagementOtherSubjectsTableData(): SagaIterator {
    yield takeLatestF('admin/GET_MANAGEMENT_OTHER_SUBJECTS_TABLE_DATA', execute);
}
